/**
 * Desktop backdrop — the area around the phone-shaped stage on a wide screen (owner change 11.9.2026: the animated
 * gradient-and-bokeh version looked cheap and is gone). It is the owner's own key art: a still by default, and the 4 s
 * loop on top of it when the machine and the viewer's settings say motion is welcome.
 *
 * The still is also the video's poster, so there is never a flash of empty colour, and the video only ever fades in over
 * an image that is already correct. It is skipped entirely on reduced motion, on Save-Data and on a slow connection —
 * the loop is 6 MB and it is decoration, never something the player waits for. It also pauses whenever the tab is
 * hidden. A phone shows the still in the bands above and below the fitted stage, at a width its viewport can use, and
 * never the loop.
 */
import { art } from '../art';
import { useEffect, useRef, useState } from 'preact/hooks';

type Conn = { saveData?: boolean; effectiveType?: string };
/** the loop is 6 MB of decoration: it is for a wide screen that actually shows the scene, never for the two thin bands
 *  above and below a fitted phone stage, and never against the viewer's motion or data settings */
const loopWelcome = (): boolean => {
  if (innerWidth < 1000) return false;
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
  const c = (navigator as Navigator & { connection?: Conn }).connection;
  if (c?.saveData) return false;
  return !/^(slow-)?2g$/.test(c?.effectiveType ?? '');
};

export function Backdrop() {
  const video = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [loop] = useState(loopWelcome);

  useEffect(() => {
    const v = video.current; if (!v || !loop) return;
    const onVisible = () => { if (document.hidden) v.pause(); else void v.play().catch(() => undefined); };
    document.addEventListener('visibilitychange', onVisible);
    void v.play().catch(() => undefined); // a browser that refuses autoplay simply leaves the still in place
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [loop]);

  return (
    <div id="backdrop" aria-hidden="true">
      <div class="bg-sky" />
      <picture>
        <source srcset={`${art("/bg/desk-1280.webp")} 1280w, ${art("/bg/desk.webp")} 2560w`} sizes="100vw" type="image/webp" />
        <img class="bg-art" src={art("/bg/desk.jpg")} alt="" draggable={false} decoding="async" />
      </picture>
      {loop && (
        <video ref={video} class={`bg-art bg-loop${playing ? ' on' : ''}`} poster={art("/bg/desk.jpg")} muted autoPlay loop playsInline preload="auto"
          disablePictureInPicture onPlaying={() => setPlaying(true)} onError={() => setPlaying(false)}>
          <source src={art("/bg/desk-loop.mp4")} type="video/mp4" />
        </video>
      )}
      <div class="bg-vignette" />
    </div>
  );
}
